import { useState } from 'react';
import { Copy, Server } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { JsonEditor } from '@/components/JsonEditor';
import {
  Button,
  Skeleton,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useClusterLogs,
  useServiceStatus,
  useClusterDeploymentLogs,
  type ServiceStatusEntry,
  type K8sDeploymentLog,
} from '@/api/k8s';

// Fallback source list used when backend hasn't returned available_sources yet
// (matches the gateway's real `available_sources`).
const DEFAULT_SOURCES = ['deployment', 'haproxy', 'cluster-autoscaler', 'controller-manager', 'nodepool-autoscaler'];
const LINE_OPTIONS = [50, 100, 200, 500];
const fmtSource = (s: string) => s.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const fmtTime = (ts?: string) => { if (!ts) return ''; const d = new Date(ts); return Number.isNaN(d.getTime()) ? ts : d.toLocaleTimeString(); };

/** Flatten the structured deployment-progress entries into aligned text lines so
 *  the "Deployment progress" view reuses the SAME code-block viewer as the raw
 *  "Deployment logs" view — one consistent log presentation, not two. */
function progressToText(logs: K8sDeploymentLog[], lastError: string): string {
  const body = logs
    .map((l) => `${fmtTime(l.timestamp).padEnd(11)} ${l.stage ? `[${l.stage}] ` : ''}${l.level ? `${l.level.toUpperCase()}  ` : ''}${l.message ?? ''}`)
    .join('\n');
  return [body, lastError && `\nError: ${lastError}`].filter(Boolean).join('\n');
}

type ActiveView = 'progress' | 'status' | 'logs';

/** Cluster logs unified viewer. SegmentedControl switches between:
 *  - Deployment progress (default): structured provisioning activity
 *  - Service status: per-node systemd service health table
 *  - Deployment logs: plain-text CodeMirror tail with source/node/line selectors */
export function Logs({ serviceId, clusterId }: { serviceId: string; clusterId: string }) {
  const { show } = useToast();
  const [view, setView] = useState<ActiveView>('progress');
  // Default node = `provisioner` so deployment/cloud-init logs are reachable on first render.
  const [node, setNode] = useState('provisioner');
  const [service, setService] = useState('deployment');
  const [lines, setLines] = useState(100);

  const { data: logsPayload, isLoading } = useClusterLogs(serviceId, clusterId, { node, service, lines });
  const { data: svcEntries = [] } = useServiceStatus(serviceId, clusterId, node || undefined);
  const { data: progressData } = useClusterDeploymentLogs(serviceId, clusterId);
  const progressErr = progressData?.last_error;
  const progressText = progressToText(
    progressData?.logs ?? [],
    typeof progressErr === 'string' ? progressErr : progressErr?.message ?? '',
  );

  // Backend returns available_nodes/available_sources alongside logs text.
  const logs = logsPayload?.logs ?? '';
  const availableNodes: string[] = logsPayload?.available_nodes ?? [];
  const availableSources: string[] = logsPayload?.available_sources ?? [];

  // Use backend-provided lists when non-empty, fall back to defaults.
  const nodeOptions = availableNodes.length > 0 ? availableNodes : [node].filter(Boolean);
  const sourceOptions = availableSources.length > 0 ? availableSources : DEFAULT_SOURCES;

  // Real service_status fields: `service` (name), `status` (running|stopped|failed),
  // `active_state`, `sub_state`. `name`/`state` kept as fallbacks for shape drift.
  const svcColumns: ColumnDef<ServiceStatusEntry, unknown>[] = [
    { id: 'svc', header: 'Service', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.service ?? row.original.name ?? '—'}</span> },
    { id: 'status', header: 'Status', cell: ({ row }) => { const s = row.original.status ?? row.original.state; const label = s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : 'Unknown'; return <StatusPill tone={/run|active/i.test(String(s ?? '')) ? 'success' : /fail|error/i.test(String(s ?? '')) ? 'danger' : 'muted'}>{label}</StatusPill>; } },
    { id: 'active', header: 'Active', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.active_state ?? '—'}</span> },
    { id: 'sub', header: 'Sub', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.sub_state ?? '—'}</span> },
  ];

  const copyText = view === 'progress' ? progressText : logs;
  const copy = () => { navigator.clipboard?.writeText(copyText).then(() => show({ tone: 'success', title: 'Logs copied' }), () => show({ tone: 'danger', title: 'Copy failed' })); };

  // Toolbar start: only mount the selectors that belong to the active view so
  // the toolbar doesn't crowd with controls irrelevant to what's displayed.
  const contextControls = view === 'status' ? (
    <Select value={node} onValueChange={setNode}>
      <SelectTrigger style={{ minWidth: 'min(200px, 100%)', maxWidth: '100%' }}><SelectValue placeholder="Select node" /></SelectTrigger>
      <SelectContent>
        {nodeOptions.map((nm, i) => <SelectItem key={nm || i} value={nm}>{nm}</SelectItem>)}
      </SelectContent>
    </Select>
  ) : view === 'logs' ? (
    <>
      <Select value={node} onValueChange={setNode}>
        <SelectTrigger style={{ minWidth: 'min(160px, 100%)', maxWidth: '100%' }}><SelectValue placeholder="Node" /></SelectTrigger>
        <SelectContent>
          {nodeOptions.map((nm, i) => <SelectItem key={nm || i} value={nm}>{nm}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={service} onValueChange={setService}>
        <SelectTrigger style={{ minWidth: 'min(150px, 100%)', maxWidth: '100%' }}><SelectValue placeholder="Source" /></SelectTrigger>
        <SelectContent>
          {sourceOptions.map((s) => <SelectItem key={s} value={s}>{fmtSource(s)}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={String(lines)} onValueChange={(v) => setLines(Number(v))}>
        <SelectTrigger style={{ minWidth: 'min(120px, 100%)', maxWidth: '100%' }}><SelectValue /></SelectTrigger>
        <SelectContent>
          {LINE_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n} lines</SelectItem>)}
        </SelectContent>
      </Select>
    </>
  ) : null;

  return (
    <>
      <Toolbar
        start={
          <>
            <SegmentedControl
              aria-label="Log view"
              value={view}
              onValueChange={(v) => setView(v as ActiveView)}
              items={[
                { value: 'progress', label: 'Cluster Logs' },
                { value: 'status', label: 'Service Status' },
                { value: 'logs', label: 'Components Logs' },
              ]}
            />
            {contextControls}
          </>
        }
        end={
          view !== 'status' ? (
            <Button variant="secondary" startIcon={<Copy size={15} />} disabled={!copyText} onClick={copy}>Copy</Button>
          ) : undefined
        }
      />

      {view === 'progress' && (
        <JsonEditor value={progressText || 'No activity yet.'} language="text" readOnly wrap maxHeight={440} ariaLabel="Deployment progress" />
      )}

      {view === 'status' && (
        svcEntries.length === 0 ? (
          <EmptyState icon={<Server size={20} />} title="No service status yet" description="Select a node with active systemd services." />
        ) : (
          <DataTable
            columns={svcColumns}
            data={svcEntries}
            getRowId={(s, i) => `${s.service ?? s.name ?? i}`}
          />
        )
      )}

      {view === 'logs' && (
        isLoading ? <Skeleton style={{ height: 200 }} /> : <JsonEditor value={logs || 'No logs available.'} language="text" readOnly wrap maxHeight={440} ariaLabel="Cluster logs" />
      )}
    </>
  );
}
